import React, { useEffect, useState, useRef } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import styled, { keyframes, css } from 'styled-components';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../utils/mobileSupported';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import Tab from '../../../components/Tab/Tab';
import BattlesTabsHeader from './BattlesTabsHeader';
import BattlesTabsScreen from './BattlesTabsScreen';
import useWindowSize from '../../../hooks/useWindowSize';
import { BATTLES_TABS } from 'shared/screens/Battles/constants/dictionary';
import { numberWithCommas } from 'shared/utils/number';
import useBattlesTabContainerStyleCalculator from '../hooks/useBattlesTabContainerStyleCalculator';
import generateRender from 'shared/utils/generateRender';

const BattlesGoal = ({ battlesAssets, setSelectedTab, goalData, selectedTab }) => {
  const { width } = useWindowSize();
  const { containerStyle } = useBattlesTabContainerStyleCalculator();
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayData, setDisplayData] = useState(goalData);
  const isFirstRender = useRef(true);
  const prevDataRef = useRef(goalData);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevDataRef.current = goalData;
      setDisplayData(goalData);
      return;
    }

    const hasDataChanged = JSON.stringify(prevDataRef.current) !== JSON.stringify(goalData);

    if (hasDataChanged) {
      setIsAnimating(true);

      const dataUpdateTimer = setTimeout(() => {
        setDisplayData(goalData);
        prevDataRef.current = goalData;
      }, 300);

      const animationResetTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 600);

      return () => {
        clearTimeout(dataUpdateTimer);
        clearTimeout(animationResetTimer);
      };
    }
  }, [goalData, selectedTab]);

  const goalBoxAsset =
    selectedTab === BATTLES_TABS.STREAK
      ? battlesAssets?.BATTLES_GRAND_REWARD_BOX
      : battlesAssets?.BATTLES_BATTLE_GOAL_BOX;

  useEffect(() => {
    if (
      !battlesAssets.BATTLES_GRAND_REWARD_BOX ||
      !battlesAssets.BATTLES_BATTLE_GOAL_BOX ||
      typeof window === 'undefined'
    )
      return;
    const urls = [battlesAssets.BATTLES_GRAND_REWARD_BOX, battlesAssets.BATTLES_BATTLE_GOAL_BOX].filter(Boolean);
    urls.forEach(src => {
      const img = new window.Image();
      img.src = src;
    });
  }, [battlesAssets.BATTLES_GRAND_REWARD_BOX, battlesAssets.BATTLES_BATTLE_GOAL_BOX]);

  return (
    <Container>
      <BattlesGoalContainer source={goalBoxAsset}>
        <AnimatedContent isAnimating={isAnimating}>
          <InfoText messageLength={displayData?.goalMessage?.length}>{displayData?.goalMessage}</InfoText>
          <InfoAmountContainer>
            <CoinIcon src={assetSource(ASSET_KEY.COIN_ICON)} alt="Coin Icon" />
            <Amount>{numberWithCommas(displayData?.rewardAmount)}</Amount>
          </InfoAmountContainer>
        </AnimatedContent>
      </BattlesGoalContainer>
      <BattlesTabsContainer>
        <Tab tabContainerTheme={containerStyle}>
          <Tab.Navigator tabAction={setSelectedTab} headerComponent={BattlesTabsHeader}>
            <Tab.Screen
              tabName={BATTLES_TABS.GAME}
              component={
                <BattlesTabsScreen
                  battlesAssets={battlesAssets}
                  rank={goalData?.rank}
                  progress={goalData?.progress}
                  points={goalData?.points}
                />
              }
              battlesAssets={battlesAssets}
              screenContainerTheme={screenContainerTheme(width)}
            />
            <Tab.Screen
              tabName={BATTLES_TABS.STREAK}
              component={
                <BattlesTabsScreen battlesAssets={battlesAssets} rank={goalData?.rank} points={goalData?.points} />
              }
              battlesAssets={battlesAssets}
              screenContainerTheme={screenContainerTheme(width)}
            />
          </Tab.Navigator>
        </Tab>
      </BattlesTabsContainer>
    </Container>
  );
};

export default BattlesGoal;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(-5%);
    opacity: 0;
  }
  50.01% {
    transform: translateX(5%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;

  ${({ isAnimating }) =>
    isAnimating &&
    css`
      animation: ${slideOut} 0.6s ease-in-out;
    `};
`;

const isMobileBrowser = isMobile && isNotStandaloneMobilePWA && !isTablet;
const isMobilePwa = isStandalonePWA && !isTablet;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: relative;
  top: unset;
  padding: ${isMobilePwa ? '3vh 4vw 3vh 4vw' : isMobileBrowser ? '13vh 0 2vh 0' : '5vw 0 2vw 0'};
  ${({ theme }) =>
    theme.screens.device('mobile-standalone') &&
    `
    width: 98%;
  `}
  ${({ theme }) =>
    theme.screens.devices(['mobile-standalone', 'mobile-browser']) &&
    `
    top: 0.5vh;
  `}
`;

const BattlesGoalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${isMobileBrowser ? '23vw' : '100%'};
  height: 100%;
  min-height: ${isMobileBrowser ? '16vh' : 'unset'};
  justify-content: center;
  align-items: center;
  background-image: url(${({ source }) => source});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  top: unset;
  overflow: hidden; /* Important for animation containment */
  ${({ theme }) =>
    theme.screens.devices(['mobile-standalone', 'mobile-browser']) &&
    `
    top: 1vh;
  `}
  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.device('mobile-standalone') &&
    `
    top: unset;
    width: 107%;
  `}
`;

const BattlesTabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${isMobileBrowser ? '23vw' : '100%'};
  height: 100%;
  justify-content: center;
  align-items: center;
  ${isMobileBrowser && 'margin-bottom: 5vh;'}
`;

const InfoText = styled.span`
  color: #ffffff;
  font-size: 1.5vw;
  text-align: center;
  line-height: 1.2;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 2.4vw;
  padding: 0 1rem;

  ${({ theme }) => theme.BattlesGoal.InfoText}

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: ${props => (props.messageLength < 31 ? 1.6 : 1.33)}vw;
  }
  ${({ theme, messageLength }) =>
    theme.screens.device('mobile-browser') && `font-size: ${messageLength < 29 ? 1.2 : 1}vw; margin-top: 2vw;`}
  ${({ theme, messageLength }) =>
    theme.screens.device('mobile-standalone') && `font-size: ${messageLength < 31 ? 1.6 : 1.4}vw;`}
`;

const InfoAmountContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.screens.device('mobile-browser') && `margin-bottom: 3px;`}
`;

const CoinMobileTopSpaceDefalt = css`
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    theme.platform('ios') &&
    `
   top: 1px;
    `}
`;

const CoinMobileTopSpaceSGA = css`
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    theme.platform('ios') &&
    `
   top: -2px;
    `}
`;

const CoinMobileTopSpace = generateRender({
  default: CoinMobileTopSpaceDefalt,
  sga: CoinMobileTopSpaceSGA,
});

const CoinIcon = styled.img`
  position: relative;
  width: 3.5vh;
  height: 3.5vh;
  margin: 0 0.25vh;
  top: 1px;

  ${({ theme }) => theme.breakpoints.only('sm')} {
    width: 2.8vh;
    height: 2.8vh;
  }

  ${({ theme }) =>
    theme.screens.devices(['mobile-browser']) &&
    ` width: 2.5vh;
    height: 2.5vh;
  `}
  ${CoinMobileTopSpace}
`;

const Amount = styled.span`
  color: #ffffff;
  font-size: 2vw;
  text-align: center;
  line-height: 1.2;
  font-weight: 700;
  text-transform: uppercase;

  ${({ theme }) => theme.BattlesGoal.Amount}
  ${({ theme }) => theme.screens.device('mobile-browser') && `font-size: 1.3vw;`}
`;

const screenContainerTheme = width => ({
  bottom: 5,
  width: isMobileBrowser ? '21vw' : '32vw',
  height: isMobileBrowser ? '14vh' : '9vw',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0',
});

